import { Component, ReactNode } from "react";
import posthog from "@/lib/posthog";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * There was no error boundary anywhere in the app before this -- a
 * render-time exception in any component just produced a blank page with
 * nothing to report it. This is a class component on purpose: React error
 * boundaries only work as componentDidCatch/getDerivedStateFromError,
 * there's no hook equivalent.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    posthog.captureException(error, { componentStack: info.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="text-lg font-medium text-gray-900 dark:text-white">Something went wrong.</p>
          <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
            We&apos;ve been notified and are looking into it. Try reloading the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
